
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Typography,
  Button,
  FormLabel,
  FormGroup,
  TextField,
  IconButton,
  Tooltip
} from '@mui/material';

import { AppDispatch } from '../../../../../store';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DocsIcon from '../../../../../assets/main/docs-icon.svg';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import DownloadIcon from '@mui/icons-material/Download';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { calculateDaysLeft, getFileNameFromPath, truncateLast } from '../../../../../helpers';
import PageCard from '../../../../../components/PageCard';
import CardTitle from '../../../../../components/CardTitle';
import GoogleMapLocationPicker, { LocationValue } from '../../../../../components/GoogleMapLocationPicker';
import SecureImage from '../../../../../components/common/SecureImage';
import { GOOGLE_MAPS_API_KEY } from '../../../../../utils/env';
import ActionPopup from '../../../../../components/common/ActionPopup';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelIcon from '@mui/icons-material/Cancel';
import apiCall from '../../../../../utils/api';
import { API_URL } from '../../../../../utils/env';

import PreferredTimes from '../../../../Worker/Onboarding/components/PreferredTimes';

import {
  BasicDetailItemContainer,
  BasicDetailItemBox,
  CustomCard,
  AddButton,
  CustomTextField,
  DocsContainer,
  DateContainer,
  DateTextField,
  ButtonGroupContainer,
  CancelButton,
  SaveButton,
  UserDetailItemContainer,
  UserWorkerPreferenceItemContainer,
  DocumentExpiring
} from '../../styles';

// redux
import {
  selectWorkerDetails,
  IQualification,
  IWorkerOnboardingDocument,
  updateWorkerPrefrence,
  IWorkerPreferenceData,
  // updateWorkerDocuments,
  deleteWorkerQualification,
  updateWorkerQualification,
  updateWorkerSkillAndExperience,
  IWorkerSkillAndExperienceData,
  mapWorkerQualification,
  IMapWorkerQualificationData,
  updateWorkerDocumentStatus,
  selectWorkerDocumentStatusUpdateLoading,
  fetchWorkerDetail,
  fetchWorkerCustomFormSubmissions,
  selectWorkerCustomFormSubmissionsData,
  fetchWorkerRoleDocumentRequirementsBulk,
} from '../../../../../modules/worker';

import {
  fetchQualificationList,
} from '../../../../../modules/qualification';

import {
  fetchRoleList,
  IRole,
} from '../../../../../modules/role';
import { FetchResponse, OptionType } from '@/interfaces/paginatedSearchSelect.interface';
import PaginatedSearchSelect from '../../../../../components/PaginatedSearchSelect';
import PaginatedMultiSelect from '../../../../../components/PaginatedMultiSelect';
import AlertPopup from '../../../../../components/common/AlertPopup';
import ConfirmationDialog from '../../../../../components/common/ConfirmationDialog';
import DocumentPreviewPopup from '../../../../../components/common/DocumentPreviewPopup';

interface IUploadedDocument extends IWorkerOnboardingDocument {
  issuedDate: dayjs.Dayjs | null;
  expiresDate: dayjs.Dayjs | null;
}

interface ISelectedQual {
  workerQualificationId: number;
  qualification: OptionType | null;
}

const toSentenceCase = (str: string) => {
  if (!str) return '';
  const result = str.split('_').join(' ');
  return result.charAt(0).toUpperCase() + result.slice(1).toLowerCase();
};

interface ProfileAndDetailsProps {
  workerId: number;
}

const ProfileAndDetails: React.FC<ProfileAndDetailsProps> = ({ workerId }) => {
  const dispatch: AppDispatch = useDispatch();
  const workerDetails = useSelector(selectWorkerDetails);

  const [selectedQualifications, setSelectedQualifications] = React.useState<ISelectedQual[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = React.useState<IUploadedDocument[]>([])
  const [selectedSkills, setSelectedSkills] = React.useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = React.useState<OptionType[]>([]);
  const [selectedShiftTiming, setSelectedShiftTiming] = React.useState<string>('');
  const [maximumAvailableDaysPerWeek, setMaximumAvailableDaysPerWeek] = React.useState<string>("")
  const [yearOfExperience, setYearOfExperienc] = React.useState<number>(0);
  const [alert, setAlert] = React.useState<{ message: string, severity: 'success' | 'error' } | null>(null);
  const [addedQualifications, setAddedQualifications] = React.useState<number[]>([]);
  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [confirmation, setConfirmation] = React.useState<{ open: boolean; title: string; message: string; onConfirm: (() => void) | null }>({ open: false, title: '', message: '', onConfirm: null });

  const customFormSubmissions = useSelector(selectWorkerCustomFormSubmissionsData);

  const [showPreviewPopup, setShowPreviewPopup] = React.useState<boolean>(false); // State for DocumentPreviewPopup visibility
  const [previewFileUrl, setPreviewFileUrl] = React.useState<string>(''); // State for the URL of the file to preview
  const [previewFileType, setPreviewFileType] = React.useState<'image' | 'pdf' | 'other'>('other'); // State for the type of file to preview
  const [previewFileName, setPreviewFileName] = React.useState<string>(''); // State for the name of the file to preview
  const [location, setLocation] = React.useState<LocationValue | null>(null);
  const [isPreferredTimesOpen, setIsPreferredTimesOpen] = React.useState(false);
  const [showRejectPopup, setShowRejectPopup] = React.useState(false);
  const [showAcceptPopup, setShowAcceptPopup] = React.useState(false);
  const [documentToReject, setDocumentToReject] = React.useState<IUploadedDocument | null>(null);
  const [documentToAccept, setDocumentToAccept] = React.useState<IUploadedDocument | null>(null);

  const documentStatusUpdateLoading = useSelector(selectWorkerDocumentStatusUpdateLoading);

  const [policeVerificationIssuedDate, setPoliceVerificationIssuedDate] = React.useState<dayjs.Dayjs | null>(null);

  const [policeVerificationExpiresDate, setPoliceVerificationExpiresDate] = React.useState<dayjs.Dayjs | null>(null);

  const [requiredDocuments, setRequiredDocuments] = React.useState<any[]>([]); // To store fetched requirements

  React.useEffect(() => {
    if (workerDetails) {
      if (workerDetails.worker_qualification && workerDetails.worker_qualification.length > 0) {
        const initialQualifications = workerDetails.worker_qualification.map(wq => ({
          workerQualificationId: wq.worker_qualifications_id,
          qualification: {
            id: wq.qualification.qualification_id,
            name: wq.qualification.qualification_name,
          }
        }));
        setSelectedQualifications(initialQualifications);
      }
      if (workerDetails.skills && workerDetails.skills.length > 0) {
        setSelectedSkills(workerDetails.skills);
      }
      if (workerDetails.worker_onboarding_documents) {
        const documents = workerDetails.worker_onboarding_documents.map(doc => ({
          ...doc,
          issuedDate: doc.document_issued_on ? dayjs(doc.document_issued_on) : null,
          expiresDate: doc.document_expiring_on ? dayjs(doc.document_expiring_on) : null,
        }));
        setUploadedDocuments(documents);
      } else {
        setUploadedDocuments([]);
      }
      if (workerDetails.worker_skills) {
        // const initialSpecializationSkills = workerDetails.worker_skills.map(wss => ({
        //   id: wss.skill.skill_id,
        //   name: wss.skill.skill_name,
        // }));
      }
      if (workerDetails.preferred_roles) {
        // Handle both array (new) and potentially single object (legacy/fallback) if needed, 
        // but type says array now.
        const roles = Array.isArray(workerDetails.preferred_roles)
          ? workerDetails.preferred_roles
          : [workerDetails.preferred_roles];

        setSelectedRoles(roles.map(r => ({
          id: r.role_id,
          name: r.role_name,
        })));
      }
      if (workerDetails.preferred_timings) {
        setSelectedShiftTiming(workerDetails.preferred_timings);
      }
      if (workerDetails.maximum_available_days_per_week) {
        setMaximumAvailableDaysPerWeek(workerDetails.maximum_available_days_per_week)
      }
      if (workerDetails.years_of_experience) {
        setYearOfExperienc(workerDetails.years_of_experience)
      }
      setPoliceVerificationIssuedDate(workerDetails.police_verification_issued_on ? dayjs(workerDetails.police_verification_issued_on) : null);
      setPoliceVerificationExpiresDate(workerDetails.police_verification_expiring_on ? dayjs(workerDetails.police_verification_expiring_on) : null);
      if (workerDetails.latitude && workerDetails.longitude && workerDetails.worker.location_name) {
        setLocation({
          lat: parseFloat(workerDetails.latitude),
          lng: parseFloat(workerDetails.longitude),
          location_name: workerDetails.worker.location_name
        })
      }
    }
  }, [workerDetails])

  React.useEffect(() => {
    if (workerId) {
      dispatch(fetchWorkerCustomFormSubmissions(workerId));
    }
  }, [workerId, dispatch]);

  // Effect to fetch required documents when roles change
  // Effect to fetch required documents when roles change
  React.useEffect(() => {
    if (selectedRoles.length > 0) {
      const roleIds = selectedRoles.map(role => Number(role.id));
      dispatch(fetchWorkerRoleDocumentRequirementsBulk(roleIds) as any)
        .then((result: any) => {
          if (fetchWorkerRoleDocumentRequirementsBulk.fulfilled.match(result)) {
            const requirements = result.payload;
            const mergedDocs = requirements.map((req: any) => {
              // Try matching by requirement id
              const matchingUpload = uploadedDocuments.find(u => u.document_type === req.requirement_id);
              return {
                ...req,
                uploaded_document: matchingUpload || null
              };
            });
            setRequiredDocuments(mergedDocs);
          }
        });
    } else {
      setRequiredDocuments([]);
    }
  }, [selectedRoles, dispatch, uploadedDocuments]);

  const fetchQualificationOptions = React.useCallback(async (search: string, page: number): Promise<FetchResponse<OptionType>> => {
    const response = await dispatch(fetchQualificationList({
      page,
      pageSize: 10,
      search,
    }) as any);

    if (response.payload) {
      const qualifications = response.payload.results.map((q: IQualification) => ({
        id: q.qualification_id,
        name: q.qualification_name,
      }));

      return {
        options: qualifications,
        hasMore: response.payload.count > page * 10,
      };
    }

    return {
      options: [],
      hasMore: false,
    };
  }, [dispatch]);

  const fetchRoleOptions = React.useCallback(async (search: string, page: number): Promise<FetchResponse<OptionType>> => {
    const response = await dispatch(fetchRoleList({
      page,
      pageSize: 10,
      search,
    }) as any);

    if (response.payload && response.payload.results) {
      const roles = response.payload.results.map((r: IRole) => ({
        id: r.role_id,
        name: r.role_name,
      }));


      return {
        options: roles,
        hasMore: response.payload.count > page * 10,
      };
    }

    return {
      options: [],
      hasMore: false,
    };
  }, [dispatch]);

  const handleAddQualification = () => {
    const tempId = -Date.now();
    setSelectedQualifications(prev => [...prev, { workerQualificationId: tempId, qualification: null }]);
  };

  const handleRemoveQualification = (index: number) => {
    const itemToRemove = selectedQualifications[index];
    setConfirmation({
      open: true,
      title: 'Confirm Deletion',
      message: 'Are you sure you want to delete this qualification?',
      onConfirm: () => {
        if (itemToRemove.workerQualificationId > 0) { // Only call delete for existing items
          dispatch(deleteWorkerQualification(itemToRemove.workerQualificationId));
        } else { // This is a newly added qualification (tempId)
          if (itemToRemove.qualification && itemToRemove.qualification.id) {
            setAddedQualifications(prev => prev.filter(id => id !== itemToRemove.qualification?.id));
          }
        }
        setSelectedQualifications(prev => prev.filter((_, i) => i !== index));
        setConfirmation({ open: false, title: '', message: '', onConfirm: null });
      }
    });
  };

  const handleQualificationChange = (index: number, newValue: OptionType | null) => {
    const item = selectedQualifications[index];
    const workerQualificationId = item.workerQualificationId;
    const oldQualificationId = item.qualification?.id; // Get the old qualification ID if it exists

    if (workerQualificationId > 0 && newValue) { // Existing item
      dispatch(updateWorkerQualification({
        workerQualificationId: workerQualificationId,
        qualificationId: newValue.id as number,
      }));
    }

    // Logic for newly added qualifications
    if (workerQualificationId < 0) { // This is a newly added qualification (tempId)
      setAddedQualifications(prev => {
        let newAdded = [...prev];
        // If there was an old qualification selected for this temp item, remove it first
        if (oldQualificationId && newAdded.includes(oldQualificationId as number)) {
          newAdded = newAdded.filter(id => id !== oldQualificationId);
        }
        // If a new qualification is selected, add its ID
        if (newValue && newValue.id) {
          newAdded.push(newValue.id as number);
        }
        return newAdded;
      });
    }

    setSelectedQualifications(prev => {
      const newQualifications = [...prev];
      newQualifications[index].qualification = newValue;
      return newQualifications;
    });
  };

  const handleAddSkill = () => {
    setSelectedSkills(prev => [...prev, '']);
  };

  const handleRemoveSkill = (index: number) => {
    setConfirmation({
      open: true,
      title: 'Confirm Deletion',
      message: 'Are you sure you want to delete this skill?',
      onConfirm: () => {
        setSelectedSkills(prev => prev.filter((_, i) => i !== index));
        setConfirmation({ open: false, title: '', message: '', onConfirm: null });
      }
    });
  };

  const handleSkillChange = (index: number, newValue: string) => {
    setSelectedSkills(prev => {
      const newSkills = [...prev];
      newSkills[index] = newValue;
      return newSkills;
    });
  };

  const handleDocumentDateChange = (index: number, field: 'issuedDate' | 'expiresDate', newValue: dayjs.Dayjs | null) => {
    setUploadedDocuments(prev => {
      const newDocuments = [...prev];
      newDocuments[index] = {
        ...newDocuments[index],
        [field]: newValue,
      };
      return newDocuments;
    });
  };

  const handleSave = async () => {
    if (!workerId) return;
    setIsSaving(true);
    try {
      const results = await Promise.all([
        updatePreference(),
        // updateDocuments(),
        updateWorkerSkillAndExperiene(),
        mapNewQualifications(),
      ]);

      const hasError = results.some(result => result.error);

      if (hasError) {
        const errorMessages = results
          .filter(result => result.error)
          .map(result => result.payload.message || 'An unknown error occurred')
          .join(', ');
        setAlert({ message: `Failed to save changes: ${errorMessages}`, severity: 'error' });
      } else {
        setAlert({ message: 'Changes saved successfully!', severity: 'success' });
      }
    } catch (error) {
      setAlert({ message: 'An unexpected error occurred while saving changes.', severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const mapNewQualifications = () => {
    if (addedQualifications.length === 0) return Promise.resolve({ error: false, payload: { message: 'No new qualifications to map' } });
    const qualificationData: IMapWorkerQualificationData = {
      worker_id: workerId,
      qualification: addedQualifications,
    };

    return dispatch(mapWorkerQualification(qualificationData) as any)
  }

  const updateWorkerSkillAndExperiene = () => {
    const updateData: IWorkerSkillAndExperienceData = {
      worker_id: workerId,
      skills: selectedSkills,
      years_of_experience: yearOfExperience
    }
    return dispatch(updateWorkerSkillAndExperience(updateData) as any)
  }

  const updatePreference = () => {

    const preferred_roles_ids = selectedRoles.map(r => parseInt(String(r.id)));
    const updateData: IWorkerPreferenceData = {
      preferred_roles: preferred_roles_ids,
      preferred_timings: selectedShiftTiming,
      maximum_available_days_per_week: parseFloat(maximumAvailableDaysPerWeek),
      worker_id: workerId,
      location_name: location?.location_name,
      longitude: location?.lng,
      latitude: location?.lat
    }

    return dispatch(updateWorkerPrefrence(updateData) as any)
  }

  // const updateDocuments = () => {
  //   const updatingDocuments = uploadedDocuments.map(doc => ({
  //     document_id: doc.documents_id,
  //     document_issued_on: (doc.issuedDate) ? doc.issuedDate?.format('YYYY-MM-DD') : "",
  //     document_expiring_on: (doc.expiresDate) ? doc.expiresDate?.format('YYYY-MM-DD') : "",
  //   }));

  //   return dispatch(updateWorkerDocuments({
  //     worker_id: workerId,
  //     documents: updatingDocuments,
  //     police_verification: {
  //       police_verification_expiring_on: policeVerificationExpiresDate?.format('YYYY-MM-DD'),
  //       police_verification_issued_on: policeVerificationIssuedDate?.format('YYYY-MM-DD'),
  //     }
  //   }) as any)
  // }

  interface BasicDetailItemProps {
    title: string;
    value: string | undefined;
  }

  const BasicDetailItem = ({ title, value }: BasicDetailItemProps) => (
    <BasicDetailItemContainer>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 0.5, fontWeight: 600 }}
      >
        {title}
      </Typography>
      <Typography
        variant="body1"
        color="text.primary"
        fontWeight="medium"
      >
        {value}
      </Typography>
    </BasicDetailItemContainer>
  )

  const getFileType = (url: string): 'image' | 'pdf' | 'other' => {
    const extension = url.split('.').pop()?.toLowerCase();
    if (extension && ['jpg', 'jpeg', 'png', 'gif'].includes(extension)) {
      return 'image';
    }
    if (extension === 'pdf') {
      return 'pdf';
    }
    return 'other';
  };

  const handleDownload = async (url: string, fileName: string) => {
    try {
      let blob: Blob;
      if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Network response was not ok');
        blob = await response.blob();
      } else {
        const response = await apiCall<Blob>(
          'get',
          `/api/fetch-s3-file/?file_path=${encodeURIComponent(url)}&file_format=blob`,
          undefined,
          undefined,
          'blob'
        );
        blob = response.data;
      }

      if (!(blob instanceof Blob)) {
        throw new Error('Failed to get a valid file');
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      const fallbackUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
      window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Box>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}

      <PageCard padding={2} backgroundRequired={true}>
        <CustomCard>
          <CardTitle title='Basic Details' />
          <Grid container spacing={2}>
            <Grid size={{ xs: 3 }}>
              <BasicDetailItem
                title='First Name'
                value={workerDetails?.user.first_name}
              />
              <BasicDetailItem
                title='Phone Number'
                value={workerDetails?.user.phone_number}
              />
              <BasicDetailItem
                title='Address'
                value={workerDetails?.user.address || '--'}
              />
            </Grid>
            <Grid size={{ xs: 3 }}>
              <BasicDetailItem
                title='Last Name'
                value={workerDetails?.user.last_name}
              />
              <BasicDetailItem
                title='Email Address'
                value={workerDetails?.user.email}
              />
            </Grid>
            <Grid size={{ xs: 3 }}>
              <BasicDetailItem
                title='Date of Birth'
                value={dayjs(workerDetails?.date_of_birth).format('DD/MM/YYYY')}
              />
              <BasicDetailItem
                title='Emergency Contact (Name)'
                value={workerDetails?.user.first_name + " " + workerDetails?.user.last_name}
              />
            </Grid>
            <Grid size={{ xs: 3 }}>
              <BasicDetailItem
                title='Gender'
                value={workerDetails?.gender}
              />
              <BasicDetailItem
                title='Emergency Contact (No.)'
                value={workerDetails?.emergency_contact}
              />
            </Grid>
          </Grid>
        </CustomCard>
        {workerDetails?.bank_details && Object.entries(workerDetails.bank_details).length > 0 &&
          <CustomCard>
            <CardTitle title='Bank Details' />
            <Grid container spacing={2}>
              {workerDetails?.bank_details && Object.entries(workerDetails.bank_details).map(([key, value]) => (
                <Grid size={{ xs: 3 }} key={key}>
                  <BasicDetailItem
                    title={toSentenceCase(key)}
                    value={String(value)}
                  />
                </Grid>
              ))}
            </Grid>
          </CustomCard>
        }

        <CustomCard>
          <CardTitle title='Qualifiction and Experience' />

          <UserDetailItemContainer>
            <Grid container spacing={0}>
              <Grid>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5, fontWeight: 600 }}
                >
                  Education / Training Certificates
                </Typography>
              </Grid>
              <Grid>
                <AddButton onClick={handleAddQualification}><AddCircleOutlineIcon /></AddButton>
              </Grid>
            </Grid>

            {selectedQualifications && selectedQualifications.length && selectedQualifications.map((qualification, index) => (
              <BasicDetailItemBox key={index}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 11 }}>
                    <PaginatedSearchSelect
                      fetchOptions={fetchQualificationOptions}
                      getOptionLabel="name"
                      value={qualification.qualification}
                      onChange={(newValue) => handleQualificationChange(index, newValue)}
                      placeholder="Select Qualification"
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 1 }}>
                    <Button
                      color="error"
                      onClick={() => handleRemoveQualification(index)}
                    >
                      <DeleteOutlineIcon />
                    </Button>
                  </Grid>
                </Grid>
              </BasicDetailItemBox>
            ))}
          </UserDetailItemContainer>

          {/* skils and Specialization */}
          <UserDetailItemContainer>
            <Grid container spacing={0}>
              <Grid >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5, fontWeight: 600 }}
                >
                  Skills / Specialization
                </Typography>
              </Grid>
              <Grid >
                <AddButton onClick={handleAddSkill}><AddCircleOutlineIcon /></AddButton>
              </Grid>
            </Grid>

            {selectedSkills && selectedSkills.length && selectedSkills.map((skill, index) => (
              <BasicDetailItemBox key={index}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 11 }} >
                    <CustomTextField
                      fullWidth
                      size='small'
                      value={skill}
                      onChange={(e) => handleSkillChange(index, e.target.value)}
                      placeholder="Enter Skill"
                    />
                  </Grid>
                  <Grid size={{ xs: 1 }}>
                    <Button
                      color="error"
                      onClick={() => handleRemoveSkill(index)}
                    >
                      <DeleteOutlineIcon />
                    </Button>
                  </Grid>
                </Grid>
              </BasicDetailItemBox>
            ))}
          </UserDetailItemContainer>

          <UserDetailItemContainer>
            <Grid container spacing={0}>
              <Grid >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5, fontWeight: 600 }}
                >
                  Years of Experience
                </Typography>
              </Grid>
            </Grid>
            <BasicDetailItemBox>
              <Grid container spacing={3}>
                <Grid size={{ xs: 11 }} >
                  <CustomTextField
                    fullWidth
                    size='small'
                    value={yearOfExperience}
                    onChange={(e) => {
                      const num = parseFloat(e.target.value);
                      setYearOfExperienc(isNaN(num) ? 0 : num);
                    }}
                  />
                </Grid>
              </Grid>
            </BasicDetailItemBox>
          </UserDetailItemContainer>
        </CustomCard>

        <CustomCard>
          <CardTitle title='Work Preference' />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <UserWorkerPreferenceItemContainer>
                <FormGroup>
                  <FormLabel
                    sx={{
                      mb: 2,
                      fontWeight: 600
                    }}
                  >
                    Prefered Job Roles / Position
                  </FormLabel>
                  <PaginatedMultiSelect
                    fetchOptions={fetchRoleOptions}
                    getOptionLabel="name"
                    value={selectedRoles}
                    onChange={(newValue: any[]) => setSelectedRoles(newValue)}
                    placeholder="Select Role"
                    fullWidth
                  />
                </FormGroup>
              </UserWorkerPreferenceItemContainer>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <UserWorkerPreferenceItemContainer>
                <FormGroup>
                  <FormLabel
                    sx={{
                      mb: 2,
                      fontWeight: 600
                    }}
                  >
                    Prefered Shift Timings
                  </FormLabel>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setIsPreferredTimesOpen(true)}
                    sx={{
                      height: '40px',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'rgba(71, 149, 119, 0.04)'
                      }
                    }}
                  >
                    View Preferences
                  </Button>
                </FormGroup>
              </UserWorkerPreferenceItemContainer>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <UserWorkerPreferenceItemContainer>
                <FormGroup>
                  <FormLabel
                    sx={{
                      mb: 2,
                      fontWeight: 600
                    }}
                  >
                    Location Preference
                  </FormLabel>

                  <GoogleMapLocationPicker
                    value={location}
                    onChange={(v) => {
                      console.log("Selected:", v);
                      setLocation(v);
                    }}
                    apiKey={GOOGLE_MAPS_API_KEY}
                    size='small'
                  />
                </FormGroup>
              </UserWorkerPreferenceItemContainer>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <UserWorkerPreferenceItemContainer>
                <FormGroup>
                  <FormLabel
                    sx={{
                      mb: 2,
                      fontWeight: 600
                    }}
                  >
                    Maximum Available Days Per Week
                  </FormLabel>
                  <TextField
                    size='small'
                    value={maximumAvailableDaysPerWeek}
                    type='number'
                    inputProps={{ min: 0, max: 7 }}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '') {
                        setMaximumAvailableDaysPerWeek('');
                        return;
                      }
                      const numVal = parseFloat(val);
                      if (numVal < 0) setMaximumAvailableDaysPerWeek('0');
                      else if (numVal > 7) setMaximumAvailableDaysPerWeek('7');
                      else setMaximumAvailableDaysPerWeek(val);
                    }}
                  />
                </FormGroup>
              </UserWorkerPreferenceItemContainer>
            </Grid>
          </Grid>
        </CustomCard>



        <CustomCard>
          <CardTitle title='Documents Uploaded' />
          {workerDetails?.police_verification &&
            <DocsContainer>
              <Grid container>
                <Grid size={{ xs: 6 }}>
                  <Grid container spacing={2}>
                    <Grid sx={{ marginTop: 1 }}>
                      <img src={DocsIcon} alt="Document Icon" />
                    </Grid>
                    <Grid>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Police Verification Documents
                      </Typography>
                      <Typography variant='subtitle2' fontWeight="body2">
                        {getFileNameFromPath(workerDetails.police_verification)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Grid container>
                    <Grid size={{ xs: 6 }}>
                      <DateContainer>
                        <FormGroup>
                          <FormLabel>
                            Issued:
                          </FormLabel>
                          <Box sx={{ position: 'relative' }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker format="DD/MM/YYYY"
                                enableAccessibleFieldDOMStructure={false}
                                value={policeVerificationIssuedDate}
                                onChange={(newValue) => setPoliceVerificationIssuedDate(newValue)}
                                slots={{ textField: DateTextField }}
                                slotProps={{
                                  textField: {
                                    variant: 'outlined',
                                    size: 'small',
                                  },
                                  popper: {
                                    disablePortal: true,
                                    sx: {
                                      position: 'absolute',
                                      top: '100%',
                                      left: '0',
                                      transform: 'none !important',
                                      zIndex: 1300,
                                    }
                                  }
                                }}
                              />
                            </LocalizationProvider>
                          </Box>
                        </FormGroup>
                        <FormGroup>
                          <Typography variant="body2" sx={{ mr: 1, ml: 1, mt: 2 }}>
                            to
                          </Typography>
                        </FormGroup>
                        <FormGroup>
                          <FormLabel>
                            Expires:
                          </FormLabel>
                          <Box sx={{ position: 'relative' }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker format="DD/MM/YYYY"
                                enableAccessibleFieldDOMStructure={false}
                                value={policeVerificationExpiresDate}
                                onChange={(newValue) => setPoliceVerificationExpiresDate(newValue)}
                                slots={{ textField: DateTextField }}
                                slotProps={{
                                  textField: {
                                    variant: 'outlined',
                                    size: 'small',
                                  },
                                  popper: {
                                    disablePortal: true,
                                    sx: {
                                      position: 'absolute',
                                      top: '100%',
                                      left: '0',
                                      transform: 'none !important',
                                      zIndex: 1300,
                                    }
                                  }
                                }}
                              />
                            </LocalizationProvider>
                          </Box>
                        </FormGroup>
                      </DateContainer>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Box flexShrink={0} textAlign="right">
                        <Tooltip title="Preview">
                          <IconButton
                            onClick={() => {
                              const fullUrl = workerDetails.police_verification;
                              if (fullUrl) {
                                setPreviewFileUrl(fullUrl);
                                setPreviewFileType(getFileType(fullUrl));
                                setPreviewFileName('Police Verification Documents');
                                setShowPreviewPopup(true);
                              }
                            }}
                          >
                            <RemoveRedEyeIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton
                            onClick={() => {
                              const fullUrl = workerDetails.police_verification;
                              if (fullUrl) {
                                handleDownload(fullUrl, getFileNameFromPath(fullUrl));
                              }
                            }}
                            color="primary"
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </DocsContainer>
          }

          {requiredDocuments &&
            requiredDocuments.length > 0 &&
            requiredDocuments.map((req, index) => {
              const document = req.uploaded_document;
              // If no document is uploaded, we still show the requirement
              const fileName = document ? getFileNameFromPath(document.document_uploaded_path) : 'No file uploaded';

              return (
                <DocsContainer key={index}>
                  <Grid container>
                    <Grid size={{ xs: 12, md: 5 }}>
                      <Grid container spacing={2} wrap="nowrap">
                        <Grid sx={{ marginTop: 1 }}>
                          <img src={DocsIcon} alt="Document Icon" />
                        </Grid>
                        <Grid>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {req.document_type?.document_type || 'Unknown Document'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, display: 'block', mb: 0.5 }}>
                            {req.worker_role?.role_name ? `Required for ${req.worker_role.role_name}` : 'Required'}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            {document ? (
                              <>
                                <Typography
                                  variant='subtitle2'
                                  fontWeight="body2"
                                  title={fileName}
                                  sx={{ cursor: 'pointer' }}
                                >
                                  {truncateLast(fileName)}
                                </Typography>
                              </>
                            ) : (
                              <Typography variant="body2" color="error">
                                Missing
                              </Typography>
                            )}
                          </Box>
                          {document?.document_description && (
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ fontStyle: 'italic', mt: 0.5, borderLeft: '2px solid #e0e0e0', pl: 1 }}>
                              Note: {document.document_description}
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid size={{ xs: 12, md: 7 }}>
                      <Grid container alignItems="center">
                        <Grid size={{ xs: 12, lg: 6 }} sx={{ pr: 2 }}>
                          <DateContainer>
                            {req.document_type?.req_issue_date && (
                              <FormGroup>
                                <FormLabel>
                                  Issued:{req.document_type?.req_issue_date && <span style={{ color: 'red' }}> *</span>}
                                </FormLabel>
                                <Box sx={{ position: 'relative' }}>
                                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker format="DD/MM/YYYY"
                                      enableAccessibleFieldDOMStructure={false}
                                      value={document ? document.issuedDate : null}
                                      disabled={!document}
                                      onChange={(newValue) => {
                                        if (document) handleDocumentDateChange(uploadedDocuments.indexOf(document), 'issuedDate', newValue)
                                      }}
                                      slots={{ textField: DateTextField }}
                                      slotProps={{
                                        textField: {
                                          variant: 'outlined',
                                          size: 'small',
                                        },
                                        popper: {
                                          disablePortal: true,
                                          sx: {
                                            position: 'absolute',
                                            top: '100%',
                                            left: '0',
                                            transform: 'none !important',
                                            zIndex: 1300,
                                          }
                                        }
                                      }}
                                    />
                                  </LocalizationProvider>
                                </Box>
                              </FormGroup>
                            )}
                            {req.document_type?.req_issue_date && req.document_type?.req_expiry_date && (
                              <FormGroup>
                                <Typography variant="body2" sx={{ mr: 1, ml: 1, mt: 2 }}>
                                  to
                                </Typography>
                              </FormGroup>
                            )}
                            {req.document_type?.req_expiry_date && (
                              <FormGroup>
                                <FormLabel>
                                  Expires:{req.document_type?.req_expiry_date && <span style={{ color: 'red' }}> *</span>}
                                </FormLabel>
                                <Box sx={{ position: 'relative' }}>
                                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker format="DD/MM/YYYY"
                                      enableAccessibleFieldDOMStructure={false}
                                      value={document ? document.expiresDate : null}
                                      disabled={!document}
                                      onChange={(newValue) => {
                                        if (document) handleDocumentDateChange(uploadedDocuments.indexOf(document), 'expiresDate', newValue)
                                      }}
                                      slots={{ textField: DateTextField }}
                                      slotProps={{
                                        textField: {
                                          variant: 'outlined',
                                          size: 'small',
                                        },
                                        popper: {
                                          disablePortal: true,
                                          sx: {
                                            position: 'absolute',
                                            top: '100%',
                                            left: '0',
                                            transform: 'none !important',
                                            zIndex: 1300,
                                          }
                                        }
                                      }}
                                    />
                                  </LocalizationProvider>
                                </Box>
                              </FormGroup>
                            )}
                          </DateContainer>
                        </Grid>
                        <Grid size={{ xs: 12, lg: 6 }}>
                          <Box flexShrink={0} display="flex" justifyContent="flex-end" alignItems="center">
                            {document && (
                              <>
                                <Tooltip title="Preview">
                                  <IconButton
                                    onClick={() => {
                                      const fullUrl = document.document_uploaded_path;
                                      setPreviewFileUrl(fullUrl);
                                      setPreviewFileType(getFileType(fullUrl));
                                      setPreviewFileName(document.document_name);
                                      setShowPreviewPopup(true);
                                    }}
                                  >
                                    <RemoveRedEyeIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Download">
                                  <IconButton
                                    onClick={() => {
                                      const fullUrl = document.document_uploaded_path;
                                      handleDownload(fullUrl, getFileNameFromPath(fullUrl));
                                    }}
                                    color="primary"
                                  >
                                    <DownloadIcon />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Accept">
                                  <span>
                                    <IconButton
                                      color="success"
                                      onClick={() => {
                                        if (req.document_type?.req_issue_date && !document.issuedDate) {
                                          setAlert({ message: 'Issued date is required for this document', severity: 'error' });
                                          return;
                                        }
                                        if (req.document_type?.req_expiry_date && !document.expiresDate) {
                                          setAlert({ message: 'Expiry date is required for this document', severity: 'error' });
                                          return;
                                        }
                                        setDocumentToAccept(document);
                                        setShowAcceptPopup(true);
                                      }}
                                      disabled={documentStatusUpdateLoading || document.is_accepted}
                                    >
                                      <CheckCircleRoundedIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Reject">
                                  <span>
                                    <IconButton
                                      color="error"
                                      onClick={() => {
                                        setDocumentToReject(document);
                                        setShowRejectPopup(true);
                                      }}
                                      disabled={documentStatusUpdateLoading || document.is_rejected}
                                    >
                                      <CancelIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  {document && (document.document_expiring_on) && req.document_type?.req_expiry_date && (() => {
                    const daysLeft = calculateDaysLeft(document.document_expiring_on);
                    if (!isNaN(daysLeft) && daysLeft <= 15) {
                      return (
                        <Box sx={{ width: '100%', mt: 2 }}>
                          <DocumentExpiring>
                            <Typography variant="body2" color="white" fontWeight="medium" ml={2}>
                              EXPIRES in {daysLeft} days! Urgent action required
                            </Typography>
                          </DocumentExpiring>
                        </Box>
                      );
                    }
                    return null;
                  })()}
                </DocsContainer>
              )
            })
          }
        </CustomCard>

        <CustomCard>
          <CardTitle title='Worker Signature' />
          <SecureImage
            src={workerDetails?.worker_signature}
            alt="Worker Signature"
            sx={{
              height: 200,
              width: '100%',
              objectFit: 'contain',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 2,
              bgcolor: 'background.paper'
            }}
          />
        </CustomCard>

        {customFormSubmissions && customFormSubmissions.length > 0 && customFormSubmissions.map((form) => (
          <CustomCard key={form.form_id}>
            <CardTitle title={form.form_name} />
            <Grid container spacing={2}>
              {form.form_feilds.map((field) => (
                <Grid size={{ xs: 3 }} key={field.field_id}>
                  <BasicDetailItem
                    title={field.field_name}
                    value={field.field_value}
                  />
                </Grid>
              ))}
            </Grid>
          </CustomCard>
        ))}
      </PageCard>

      <PageCard padding={2} backgroundRequired={false}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Box flexShrink={0} textAlign="right">
              <ButtonGroupContainer>
                <CancelButton variant="outlined" href={'/worker/' + workerId}>
                  Cancel
                </CancelButton>
                <SaveButton
                  variant="contained"
                  onClick={handleSave}
                  loading={isSaving}
                >
                  {isSaving ? 'Saving details...' : 'Save Changes'}
                </SaveButton>
              </ButtonGroupContainer>
            </Box>
          </Grid>
        </Grid>
      </PageCard>

      <ConfirmationDialog
        open={confirmation.open}
        onClose={() => setConfirmation({ open: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmation.onConfirm || (() => { })}
        title={confirmation.title}
        message={confirmation.message}
      />
      <ActionPopup
        open={showRejectPopup}
        onClose={() => {
          setShowRejectPopup(false);
          setDocumentToReject(null);
        }}
        onSubmit={async (remark) => {
          if (!documentToReject || !workerId) return;

          const result = await dispatch(updateWorkerDocumentStatus({
            documentId: documentToReject.documents_id,
            action: 'reject',
            remarks: remark,
          }) as any);

          if (updateWorkerDocumentStatus.fulfilled.match(result)) {
            setAlert({ message: result.payload.message || 'Document rejected successfully!', severity: 'success' });
            dispatch(fetchWorkerDetail(workerId) as any); // Refresh worker details
          } else {
            setAlert({ message: (result.payload as string) || 'Failed to reject document.', severity: 'error' });
          }

          setShowRejectPopup(false);
          setDocumentToReject(null);
        }}
        title="Reject Document"
        message={`Are you sure you want to reject the document "${documentToReject?.document_name}"? Please provide a remark.`}
        submitButtonText="Reject"
        textFieldLabel="Rejection Remark"
        textFieldPlaceholder="Enter reason for rejection"
        loading={documentStatusUpdateLoading}
        required={true}
      />
      <ActionPopup
        open={showAcceptPopup}
        onClose={() => {
          setShowAcceptPopup(false);
          setDocumentToAccept(null);
        }}
        onSubmit={async (remark) => {
          if (!documentToAccept || !workerId) return;

          const result = await dispatch(updateWorkerDocumentStatus({
            documentId: documentToAccept.documents_id,
            action: 'accept',
            remarks: remark,
            document_issued_on: documentToAccept.issuedDate ? documentToAccept.issuedDate.format('YYYY-MM-DD') : '',
            document_expiring_on: documentToAccept.expiresDate ? documentToAccept.expiresDate.format('YYYY-MM-DD') : '',
          }) as any);

          if (updateWorkerDocumentStatus.fulfilled.match(result)) {
            setAlert({ message: result.payload.message || 'Document accepted successfully!', severity: 'success' });
            dispatch(fetchWorkerDetail(workerId) as any); // Refresh worker details
          } else {
            setAlert({ message: (result.payload as string) || 'Failed to accept document.', severity: 'error' });
          }

          setShowAcceptPopup(false);
          setDocumentToAccept(null);
        }}
        title="Accept Document"
        message={`Are you sure you want to accept the document "${documentToAccept?.document_name}"?`}
        submitButtonText="Accept"
        loading={documentStatusUpdateLoading}
        required={false}
      />
      <DocumentPreviewPopup
        open={showPreviewPopup}
        onClose={() => setShowPreviewPopup(false)}
        fileUrl={previewFileUrl}
        fileType={previewFileType}
        fileName={previewFileName}
      />
      <PreferredTimes
        open={isPreferredTimesOpen}
        onClose={() => setIsPreferredTimesOpen(false)}
        workerId={workerId}
      />
    </Box>
  );
};

export default ProfileAndDetails;
